import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MenuSectionConfig } from './menuConfig';

interface GenericMegaMenuProps {
  isOpen: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  config: MenuSectionConfig;
}

export const GenericMegaMenu: React.FC<GenericMegaMenuProps> = ({
  isOpen,
  onMouseEnter,
  onMouseLeave,
  config
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ 
            duration: 0.25, 
            ease: [0.25, 0.1, 0.25, 1]
          }}
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-[100]"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {/* Solid container with subtle shadow */}
          <div className={cn(
            "w-[420px] p-3 rounded-2xl overflow-hidden",
            "bg-[#FDFCFB]",
            "border border-border/40",
            "shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25),0_0_0_1px_rgba(0,0,0,0.05)]"
          )}>
            {/* Menu items - scrollable */}
            <div className="space-y-0.5 max-h-[60vh] overflow-y-auto pr-1 scrollbar-thin">
              {config.items.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 + 0.1 }}
                >
                  <Link
                    to={item.path}
                    className={cn(
                      "group flex items-center gap-3 p-2.5 rounded-lg",
                      "transition-all duration-300",
                      "hover:bg-accent/15",
                      "hover:shadow-md"
                    )}
                  >
                    {/* Icon container */}
                    <div 
                      className={cn(
                        "flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center",
                        "bg-white shadow-sm border border-border/30",
                        "group-hover:scale-110 group-hover:shadow-md transition-all duration-300"
                      )}
                      style={{ 
                        boxShadow: `0 4px 12px -4px ${item.accentColor}30`
                      }}
                    >
                      <item.icon 
                        className="w-4 h-4 transition-colors" 
                        style={{ color: item.accentColor }}
                        strokeWidth={1.5}
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-1">
                          {item.title}
                        </h4>
                        <ArrowRight 
                          className="w-3.5 h-3.5 text-muted-foreground/50 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" 
                        />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Footer CTA */}
            {config.footerCta && (
              <div className="mt-4 pt-3 border-t border-border/30">
                <Link
                  to={config.footerCta.path}
                  className={cn(
                    "flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg",
                    "bg-accent/10 text-accent hover:bg-accent hover:text-white",
                    "text-sm font-medium transition-all duration-300",
                    "group"
                  )}
                >
                  <span>{config.footerCta.label}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GenericMegaMenu;
